/**
 * Analytics-service transfer rows → CM Member 360 Transfer Statements
 * (Agent-UI `api/map/transfers` + `memberDetailLab` Lab helpers).
 */
import {
  paiseToDisplay,
  parseCurrencyType,
  type CurrencyType,
} from '@/features/hierarchy/hierarchy'

export const LAB_TRANSFER_MAX_DAYS = 15

export type AnalyticsTransferRow = {
  eventId: string
  occurredAt: string
  targetId: string
  targetName?: string
  targetRole?: string
  parentId?: string | null
  parentName?: string | null
  direction: 'give' | 'take'
  amountMinor: string
  targetBalanceMinor?: string
}

export type AnalyticsTransfersPage = {
  asOf?: string
  martStale?: boolean
  page?: number
  size?: number
  total?: number
  rows?: AnalyticsTransferRow[]
}

export type CmTransferParty = { id: string; username: string }

export type CmTransferRow = {
  id: string
  at: string
  amount: number
  direction: 'give' | 'take'
  fromId: string
  toId: string
  from: CmTransferParty | null
  to: CmTransferParty | null
}

export type CmTransferResult = {
  rows: CmTransferRow[]
  total: number
  page: number
  pageSize: number
  totals: { given: number; taken: number; net: number; count: number }
  asOf?: string
  martStale?: boolean
}

function stubParty(id: string, name: string): CmTransferParty {
  return { id, username: name || id.slice(0, 8) || '—' }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Map one analytics transfer row (give = parent→target, take = target→parent). */
export function toCmTransferRowView(
  row: AnalyticsTransferRow,
  currency?: string,
): CmTransferRow {
  const ccy = parseCurrencyType(currency)
  const parentId = row.parentId ?? ''
  const targetId = row.targetId
  const amount = paiseToDisplay(row.amountMinor, ccy)
  const parentName = row.parentName?.trim() || parentId.slice(0, 8) || 'parent'
  const targetName = row.targetName?.trim() || targetId.slice(0, 8)
  const target = stubParty(targetId, targetName)
  const parent = parentId ? stubParty(parentId, parentName) : null

  if (row.direction === 'give') {
    return {
      id: row.eventId,
      at: row.occurredAt,
      amount,
      direction: 'give',
      fromId: parentId,
      toId: targetId,
      from: parent,
      to: target,
    }
  }

  return {
    id: row.eventId,
    at: row.occurredAt,
    amount,
    direction: 'take',
    fromId: targetId,
    toId: parentId,
    from: target,
    to: parent,
  }
}

export function toCmTransferResult(
  pages: AnalyticsTransfersPage[],
  rootId: string,
  currency?: string,
): Omit<CmTransferResult, 'page' | 'pageSize'> {
  const first = pages[0]
  const rows = pages
    .flatMap((p) => p.rows ?? [])
    .map((row) => toCmTransferRowView(row, currency))
    .sort((a, b) => b.at.localeCompare(a.at))

  const totals = rows.reduce(
    (acc, t) => {
      if (t.fromId === rootId) acc.given += t.amount
      else if (t.toId === rootId) acc.taken += t.amount
      acc.count += 1
      return acc
    },
    { given: 0, taken: 0, net: 0, count: 0 },
  )
  totals.given = round2(totals.given)
  totals.taken = round2(totals.taken)
  totals.net = round2(totals.taken - totals.given)

  return {
    rows,
    total: rows.length,
    totals,
    asOf: first?.asOf,
    martStale: first?.martStale,
  }
}

/** Lab: payee sees amount as negative (Player Loss). */
export function labTransferSignedAmount(
  amount: number,
  toUsername: string | null | undefined,
  memberUsername: string,
): number {
  return toUsername === memberUsername ? -amount : amount
}

/** Lab Total Net Share = given − taken. */
export function labTransferNetShare(given: number, taken: number): number {
  return Math.round((given - taken) * 100) / 100
}

/**
 * Lab Account Statement Transfer Statement type from the viewer's seat
 * (`viewTransactionNew`): toUser → Taken, fromUser → Given.
 */
export function labTransferTypeForViewer(
  row: Pick<CmTransferRow, 'fromId' | 'toId'>,
  viewerId: string,
): 'Taken' | 'Given' | null {
  if (!viewerId) return null
  if (row.toId === viewerId) return 'Taken'
  if (row.fromId === viewerId) return 'Given'
  return null
}

export function labTransferRangeDays(fromDay: string, toDay: string): number {
  const from = Date.parse(`${fromDay.slice(0, 10)}T00:00:00`)
  const to = Date.parse(`${toDay.slice(0, 10)}T00:00:00`)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0
  return Math.ceil(Math.abs(to - from) / 86_400_000)
}

export function labTransferPayerPayee(
  fromUsername: string | null | undefined,
  toUsername: string | null | undefined,
): string {
  const from = fromUsername?.trim() || '—'
  const to = toUsername?.trim() || '—'
  return `${from} ( ${to} )`
}

export function emptyCmTransferResult(
  page: number,
  pageSize: number,
): CmTransferResult {
  return {
    rows: [],
    total: 0,
    page,
    pageSize,
    totals: { given: 0, taken: 0, net: 0, count: 0 },
  }
}

/** Parse one CM-API peeled analytics page (or bare rows). */
export function analyticsTransfersPageFromLive(
  live: Record<string, unknown>,
): AnalyticsTransfersPage {
  const rowsRaw = Array.isArray(live.rows) ? live.rows : []
  return {
    asOf: typeof live.asOf === 'string' ? live.asOf : undefined,
    martStale: typeof live.martStale === 'boolean' ? live.martStale : undefined,
    page: typeof live.page === 'number' ? live.page : undefined,
    size: typeof live.size === 'number' ? live.size : undefined,
    total: typeof live.total === 'number' ? live.total : undefined,
    rows: rowsRaw as AnalyticsTransferRow[],
  }
}

export type { CurrencyType }
